import React from "react";
import LegalLayout, { Section } from "@/pages/legal/LegalLayout";
import { Lock, ShieldCheck, CreditCard, Server, KeyRound, EyeOff } from "lucide-react";

const BADGES = [
  { icon: CreditCard, title: "Payments by Stripe", desc: "All billing runs through Stripe. We never see or store your full card number." },
  { icon: Lock, title: "Encrypted in transit", desc: "Every request is served over TLS/HTTPS with modern 256-bit encryption." },
  { icon: KeyRound, title: "Scoped access tokens", desc: "Social connections use revocable, least-privilege tokens — never your passwords." },
  { icon: EyeOff, title: "No data resale", desc: "We never sell your data, your audience, or engagement. Ever." },
];

export default function Security() {
  return (
    <LegalLayout title="Security & Data Handling" updated="June 2026" testid="security-page">
      <p className="text-sm text-muted-foreground leading-relaxed mb-8">
        You're trusting us with your brand and your billing details. Here's exactly how we protect both —
        in plain language, no jargon.
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-10" data-testid="security-badges">
        {BADGES.map((b, i) => (
          <div key={i} className="rounded-xl border border-border bg-card p-4 flex items-start gap-3" data-testid={`security-badge-${i}`}>
            <div className="w-10 h-10 rounded-lg bg-emerald-500/15 text-emerald-400 flex items-center justify-center shrink-0"><b.icon className="h-5 w-5" /></div>
            <div>
              <h3 className="font-semibold text-foreground text-sm">{b.title}</h3>
              <p className="text-xs text-muted-foreground mt-1 leading-relaxed">{b.desc}</p>
            </div>
          </div>
        ))}
      </div>

      <Section title="Payments & PCI compliance">
        <p>
          All payments are processed by <strong>Stripe</strong>, a PCI-DSS Level 1 certified payment provider — the
          highest level of certification in the payments industry. Your card details are sent directly to Stripe over
          an encrypted connection and are <strong>never stored on our servers</strong>. We only keep a Stripe customer
          reference so you can manage or cancel your plan. You can cancel anytime from the billing page or the Stripe
          customer portal.
        </p>
      </Section>

      <Section title="Encryption">
        <p>
          Every connection to AutoGridSocial is encrypted end-to-end using <strong>TLS 1.2+ (256-bit)</strong>.
          Data is encrypted in transit between your browser, our servers, and every third-party API we call.
          Stored credentials and access tokens are held in access-controlled databases.
        </p>
      </Section>

      <Section title="Your social accounts">
        <p>
          When you connect Facebook or LinkedIn, we use <strong>scoped, revocable access tokens</strong> — we never
          ask for or store your social passwords. Tokens grant only the permission needed to publish on your behalf,
          and you can revoke them at any time from the social network or by disconnecting in Settings. Prefer zero
          connections? Our one-tap <strong>Quick Publish</strong> lets you post from your own logged-in account with no
          tokens at all.
        </p>
      </Section>

      <Section title="What we store — and what we don't">
        <p>
          We store the content you create (workspaces, posts, campaigns) so the product works, plus the minimum
          account data needed to run your subscription. We do <strong>not</strong> sell your data, your audience, or
          engagement metrics to anyone, and we do not run third-party advertising trackers on your content.
        </p>
      </Section>

      <Section title="AI processing">
        <p>
          Post generation is powered by trusted large-language-model providers. We send only the brand context and
          source material needed to generate your posts. Your content is used to serve <em>you</em> — never to sell
          followers, fabricate engagement, or produce the generic "AI slop" this product was built to eliminate.
        </p>
      </Section>

      <Section title="Report a vulnerability">
        <p>
          Found a security issue? We want to hear about it. Email{" "}
          <a href="mailto:beckettseb367@gmail.com" className="underline hover:text-foreground">beckettseb367@gmail.com</a>{" "}
          and we'll respond promptly.
        </p>
      </Section>
    </LegalLayout>
  );
}
